<script>
import { registerUser } from "@/services/userServices"
import { getAllClinics } from "@/services/clinicServices"
import { getAllOrg } from "@/services/orgServices"
import { useAuthStore } from '@/stores/authStore'
import { getStateHealthOrgs } from "@/services/stateHealthOrgServices"

export default {
  data() {
    return {
      authStore: useAuthStore(),
      user_data: {
        username: "",
        email: "",
        password: "",
        full_name: "",
        role: "",
        profession: "",
        specialization: "",
        clinic_name: "",
        contact_number: "",
        locations: [],
        additional_info: {},
        OrganizationUuid: '',
      },
      selectedClinic: "",
      clinicsJson: {},
      clinicsSelection: [],
      showPassword: false,
      organizations: [],
      selectedRole: "clinic_admin",
      stateHealthOrgs: [],
      selectedStateHealthOrgUuid: "",
      validationError: "",
      isSubmitting: false,
    }
  },
  computed: {
    accountTypeLabel() {
      if (this.authStore.userRole === 'admin') {
        return this.selectedRole === 'state_health_org' ? 'state health organization account' : 'clinic admin account';
      }
      return 'staff account';
    },
    assignmentLabel() {
      if (this.authStore.userRole === 'admin') {
        return this.selectedRole === 'state_health_org' ? 'State organization' : 'Organization';
      }
      return 'Assigned clinics';
    },
  },
  watch: {
    selectedClinic(newClinic) {
      if (newClinic && !this.user_data.locations.includes(newClinic)) {
        this.user_data.locations.push(newClinic);
        this.syncClinicName();
        this.selectedClinic = "";
      }
    },
    'user_data.email'(email) {
      if (!this.user_data.username && email.includes('@')) {
        this.user_data.username = email.split('@')[0].replace(/\s+/g, '').toLowerCase();
      }
    },
  },
  mounted() {
    if (this.authStore.userRole === 'clinic_admin') {
      this.get_clinics();
    } else if (this.authStore.userRole === 'admin') {
      this.getOrg();
      this.getStateOrgs();
    }
  },
  methods: {
    validateRequiredFields() {
      if (!this.user_data.full_name.trim()) {
        this.validationError = "Full name is required.";
        return false;
      }
      if (!this.user_data.email.trim()) {
        this.validationError = "Email is required.";
        return false;
      }
      if (!this.user_data.username.trim()) {
        this.validationError = "Username is required.";
        return false;
      }
      if (!this.user_data.password.trim()) {
        this.validationError = "Temporary password is required.";
        return false;
      }
      if (this.authStore.userRole === 'admin') {
        if (this.selectedRole === 'clinic_admin' && !this.user_data.OrganizationUuid) {
          this.validationError = "Choose the organization this clinic admin belongs to.";
          return false;
        }
        if (this.selectedRole === 'state_health_org' && !this.selectedStateHealthOrgUuid) {
          this.validationError = "Choose the state organization for this account.";
          return false;
        }
      }
      if (this.authStore.userRole === 'clinic_admin' && this.user_data.locations.length === 0) {
        this.validationError = "Assign at least one clinic.";
        return false;
      }

      this.validationError = "";
      return true;
    },

    async submit() {
      if (this.authStore.userRole === 'admin') {
        this.user_data.role = this.selectedRole;
      } else {
        this.user_data.role = 'user';
      }

      if (this.user_data.role === 'state_health_org') {
        this.user_data.OrganizationUuid = '';
        this.user_data.locations = [];
        this.user_data.clinic_name = '';
        this.user_data.additional_info = {
          ...(this.user_data.additional_info || {}),
          StateHealthOrgUuid: this.selectedStateHealthOrgUuid,
        };
      } else {
        this.syncClinicName();
      }

      if (!this.validateRequiredFields()) {
        return;
      }

      this.isSubmitting = true;
      try {
        const response = await registerUser(this.user_data);
        if(response) {
          this.$router.push('/users');
        }
      } catch (error) {
        if (error.response && error.response.data.detail === "Username or Email already registered") {
          this.validationError = "Username or email is already registered. Try another credential.";
        } else {
          this.validationError = "Unable to create account. Please try again.";
        }
      } finally {
        this.isSubmitting = false;
      }
    },

    async get_clinics() {
      const result = await getAllClinics();
      if (result.data) {
        this.clinicsSelection = result.data.map((clinic) => {
          this.clinicsJson[clinic.LocationUuid] = clinic.ClinicName;
          return {
            name: clinic.ClinicName,
            uuid: clinic.LocationUuid,
          };
        });
      }
    },

    async getOrg() {
      const result = await getAllOrg();
      if (result.data) {
        this.organizations = result.data;
      }
    },

    async getStateOrgs() {
      const result = await getStateHealthOrgs();
      this.stateHealthOrgs = result.data || [];
    },

    remove_clinic(index) {
      this.user_data.locations.splice(index, 1);
      this.syncClinicName();
    },

    syncClinicName() {
      const firstClinicUuid = this.user_data.locations[0];
      this.user_data.clinic_name = firstClinicUuid ? this.clinicsJson[firstClinicUuid] || "" : "";
    },

    noSpaces(event) {
      this.user_data.username = event.target.value.replace(/\s+/g, '').toLowerCase();
    },

    allowOnlyDigits(event) {
      this.user_data.contact_number = event.target.value.replace(/\D/g, '');
    },

    togglePasswordVisibility() {
      this.showPassword = !this.showPassword;
    }
  }
}
</script>

<template>
  <div class="admin-page">
    <div class="page-hero">
      <div>
        <p class="page-kicker">Account Management</p>
        <h1 class="page-title">Create account</h1>
        <p class="page-subtitle">
          Create a {{ accountTypeLabel }} with only the essentials first. Profile details can be completed later.
        </p>
      </div>
      <button class="ui-button secondary" @click="$router.push('/users')">
        <i class="fas fa-arrow-left"></i> Back to accounts
      </button>
    </div>

    <div class="ui-card">
      <div class="ui-card-body">
        <div v-if="validationError" class="alert-message">
          <i class="fas fa-exclamation-circle"></i> {{ validationError }}
        </div>

        <section>
          <div class="section-heading">
            <div>
              <h2 class="section-title">Login identity</h2>
              <p class="section-description">These fields let the person sign in. Username auto-fills from email, but can be edited.</p>
            </div>
          </div>

          <div class="form-grid">
            <div class="form-section">
              <label class="field-label">Full name <span class="required">*</span></label>
              <input class="ui-input" v-model="user_data.full_name" placeholder="e.g. Nur Aisyah Ahmad">
            </div>

            <div class="form-section">
              <label class="field-label">Email <span class="required">*</span></label>
              <input type="email" class="ui-input" v-model="user_data.email" placeholder="name@clinic.com">
            </div>

            <div class="form-section">
              <label class="field-label">Username <span class="required">*</span></label>
              <input class="ui-input" v-model="user_data.username" placeholder="Auto-generated from email" @input="noSpaces">
              <span class="field-hint">No spaces. This can be changed before saving.</span>
            </div>

            <div class="form-section">
              <label class="field-label">Temporary password <span class="required">*</span></label>
              <div class="input-with-action">
                <input :type="showPassword ? 'text' : 'password'" class="ui-input" v-model="user_data.password" placeholder="Set a temporary password">
                <button type="button" class="input-action" @click="togglePasswordVisibility">
                  <i class="fas" :class="showPassword ? 'fa-eye-slash' : 'fa-eye'"></i>
                </button>
              </div>
              <span class="field-hint">Best future UX: send an invite/reset link instead of manual passwords.</span>
            </div>
          </div>
        </section>

        <section class="mt-4">
          <div class="section-heading">
            <div>
              <h2 class="section-title">Assignment</h2>
              <p class="section-description">Connect this account to the correct workspace access.</p>
            </div>
            <span class="badge-soft">{{ accountTypeLabel }}</span>
          </div>

          <div class="form-grid">
            <div class="form-section full-width" v-if="authStore.userRole === 'admin'">
              <label class="field-label">Account role <span class="required">*</span></label>
              <select class="ui-select" v-model="selectedRole">
                <option value="clinic_admin">Clinic admin</option>
                <option value="state_health_org">State health org</option>
              </select>
              <span class="field-hint">Clinic admin manages one organization. State health org is read-only and sees watchable clinics.</span>
            </div>

            <div class="form-section full-width" v-if="authStore.userRole === 'admin'">
              <label class="field-label">{{ assignmentLabel }} <span class="required">*</span></label>
              <select v-if="selectedRole === 'clinic_admin'" class="ui-select" v-model="user_data.OrganizationUuid">
                <option value="">Select organization</option>
                <option v-for="org in organizations" :key="org.OrganizationUuid" :value="org.OrganizationUuid">
                  {{ org.OrganizationName }}
                </option>
              </select>
              <select v-else class="ui-select" v-model="selectedStateHealthOrgUuid">
                <option value="">Select state organization</option>
                <option v-for="org in stateHealthOrgs" :key="org.StateHealthOrgUuid" :value="org.StateHealthOrgUuid">
                  {{ org.Name }} ({{ org.StateName }})
                </option>
              </select>
              <span v-if="selectedRole === 'clinic_admin'" class="field-hint">This clinic admin can manage the selected organization.</span>
              <span v-else class="field-hint">This state org account will only see clinics assigned as watchable by admin.</span>
            </div>

            <div class="form-section full-width" v-else-if="authStore.userRole === 'clinic_admin'">
              <label class="field-label">{{ assignmentLabel }} <span class="required">*</span></label>
              <div class="tag-list">
                <span v-if="user_data.locations.length === 0" class="table-secondary">No clinics assigned yet.</span>
                <span class="tag-pill" v-for="(clinic_id, ind) in user_data.locations" :key="clinic_id">
                  {{ clinicsJson[clinic_id] }}
                  <button type="button" @click="remove_clinic(ind)"><i class="fas fa-times"></i></button>
                </span>
              </div>
              <select class="ui-select mt-2" v-model="selectedClinic">
                <option value="">Add clinic</option>
                <option v-for="clinic in clinicsSelection" :key="clinic.uuid" :value="clinic.uuid">
                  {{ clinic.name }}
                </option>
              </select>
            </div>
          </div>
        </section>

        <section class="mt-4">
          <div class="section-heading">
            <div>
              <h2 class="section-title">Profile details</h2>
              <p class="section-description">Optional details for directory and support context.</p>
            </div>
            <span class="optional-pill">Optional</span>
          </div>

          <div class="form-grid three">
            <div class="form-section">
              <label class="field-label">Contact number</label>
              <input class="ui-input" v-model="user_data.contact_number" placeholder="Digits only" @input="allowOnlyDigits">
            </div>
            <div class="form-section">
              <label class="field-label">Profession</label>
              <input class="ui-input" v-model="user_data.profession" placeholder="e.g. Doctor, Admin">
            </div>
            <div class="form-section">
              <label class="field-label">Specialization</label>
              <input class="ui-input" v-model="user_data.specialization" placeholder="e.g. Ophthalmology">
            </div>
          </div>
        </section>

        <div class="ui-actions">
          <button class="ui-button secondary" @click="$router.push('/users')">Cancel</button>
          <button class="ui-button primary" @click="submit()" :disabled="isSubmitting">
            <i class="fas" :class="isSubmitting ? 'fa-spinner fa-spin' : 'fa-plus'"></i>
            {{ isSubmitting ? 'Creating...' : 'Create account' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
